// Core lib dependencies.
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Component dependencies.
import CaptchaInterface from '../../Captcha/index.js';
import OverlayInterface from '../../Overlay/index.js';
import { Button } from '../../../../common/components/buttons.jsx';

// Misc dependencies.
import { getLabel } from '../../../../common/utils/string.js';

// Actions.
import Actions from '../actions.js';

// Selectors.
import Selectors from '../selectors.js';

// Config.
import Config from '../config/config.js';
import Enums from '../config/enums.js';

// Model entities.
import DisplayItem from '../../../../common/model/entity/DisplayItem/DisplayItem.js';

// Legacy dependencies.
import { fbLogin } from '../../../../legacy/utils/utils.js';

/**
 * <pre>
 * Voting React component.
 *
 * Summary of responsibilities:
 *
 *  - smart component (connected to Redux store).
 *  - composes child components as required to output Voting elements of a display item.
 *  - handles events received from child components and calls mapped dispatch functions as required.
 *
 * </pre>
 *
 * @author Tim Loram
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:Voting.Components
 * @namespace
 *
 * @return {Voting.Components.Voting}
 */
class Voting extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Initial state.
    this.state = {
      isVoting:      false,
      showRecaptcha: false,
    };

    // Bind methods.
    this.handleVoteClicked = this.handleVoteClicked.bind(this);
    this.handleShowRecaptchaOverlay = this.handleShowRecaptchaOverlay.bind(this);
    this.handleCloseCaptchaOverlay = this.handleCloseCaptchaOverlay.bind(this);
    this.handleCancelCaptchaOverlay = this.handleCancelCaptchaOverlay.bind(this);
    this.handleCaptchaResponse = this.handleCaptchaResponse.bind(this);
    this.handleClosePostVoteOverlay = this.handleClosePostVoteOverlay.bind(this);
    this.handleCloseMessageOverlay = this.handleCloseMessageOverlay.bind(this);
    this.handleCloseOverlay = this.handleCloseOverlay.bind(this);
    this.processFraudStrategy = this.processFraudStrategy.bind(this);
    this.processVotingStrategy = this.processVotingStrategy.bind(this);
    this.submitVote = this.submitVote.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   */
  componentDidMount() {}

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:Voting.Components.Voting}
   */
  render() {
    // Get flags for showing elements.
    var _showButton = this.props.showVoteButton,
      _showCount = this.props.showVoteCount;

    var _voteButton, _divider, _voteCount;

    // If nothing to show, return immediately.
    if (!_showButton && !_showCount) return false;

    // Show buttons if required.
    if (_showButton) {
      _voteButton = this.renderVoteButton();
    }

    // Show count if required.
    if (_showCount && _showCount !== 'never') {
      if (_showButton) {
        _divider = <li className="xDivider">|</li>;
      }

      _voteCount = this.renderVoteCount(_showCount);
    }

    // If we are currently in the process of voting, allow overlays to be triggered.
    let _captchaOverlay;
    let _postVoteOverlay;
    let _messageOverlay;
    if (this.state.isVoting) {
      // Show recaptcha overlay if required.
      if (this.state.showRecaptcha || this.props.showDynRecaptcha) {
        _captchaOverlay = this.renderCaptcha(this.handleCancelCaptchaOverlay, this.handleCaptchaResponse);
      }

      // Show post vote CTA overlay if required.
      if (this.props.postVoteCTAContent) {
        _postVoteOverlay = this.renderOverlay(this.handleClosePostVoteOverlay, null, this.props.postVoteCTAContent);
      }

      // Show any server message overlay if required.
      if (this.props.message) {
        _messageOverlay = this.renderOverlay(this.handleCloseMessageOverlay, this.props.message);
      }
    }

    return (
      <ul className="xToolbar xRichToolbar">
        {_voteButton}
        {_divider}
        {_voteCount}

        {_captchaOverlay}
        {_postVoteOverlay}
        {_messageOverlay}
      </ul>
    );
  }

  /**
   * Renders just vote button element.
   *
   * @return {ReactElement} The vote button element.
   */
  renderVoteButton() {
    const _buttonLabel = this.props.voted ? getLabel('button.postvote') : getLabel('button.prevote');

    return (
      <li className="xAction xActionButton xActionVote">
        <Button onClick={this.handleVoteClicked} disabled={this.props.voting ? true : false}>
          {_buttonLabel}
        </Button>
      </li>
    );
  }

  /**
   * Renders just vote count element.
   *
   * @param {string|boolean} showCount Value indicating how to show the vote count.
   * @return {ReactElement} The vote count element.
   */
  renderVoteCount(showCount) {
    let _voteCount = false;

    if (showCount === 'always' || showCount === true || (showCount === 'afterVote' && this.props.voted)) {
      _voteCount = (
        <li className="xActionMeta xVoteCount">
          <span>{this.props.votes}</span>
        </li>
      );
    }

    return _voteCount;
  }

  /**
   * Render an Overlay component.
   *
   * @param {function} closeHandler The handler method to be called when overlay is closed.
   * @param {string} message The message to output inside the overlay.
   * @param {ReactElement} children Any child elements to output inside the overlay.
   * @return {module:Overlay.Components.Overlay}
   */
  renderOverlay(closeHandler, message, children = null) {
    let _overlay;

    // Do not attempt to render an Overlay without content.
    if (message || children) {
      let overlayContent;
      if (message) {
        overlayContent = (
          <div className="xCopyContainer">
            <p className="xCopy">{message}</p>
          </div>
        );
      } else {
        overlayContent = <div dangerouslySetInnerHTML={{ __html: children }} />;
      }

      _overlay = (
        <OverlayInterface.Components.Overlay closeHandler={closeHandler}>
          {overlayContent}
        </OverlayInterface.Components.Overlay>
      );
    }

    return _overlay;
  }

  /**
   * Render a Captcha component inside an Overlay.
   *
   * @param {function} closeHandler The handler method to be called when overlay is closed.
   * @param {function} responseHandler The handler method to be called when Captcha is completed.
   * @return {module:Captcha.Components.Captcha}
   */
  renderCaptcha(closeHandler, responseHandler) {
    const _overlay = (
      <OverlayInterface.Components.Overlay childrenHandleClose={true} closeHandler={closeHandler}>
        <CaptchaInterface.Components.Captcha
          responseHandler={responseHandler}
          fraudStrategy={NGXD.getServiceContainer().getService('config').get('strategies.votingFraud')}
        />
      </OverlayInterface.Components.Overlay>
    );

    return _overlay;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when vote button is clicked.
   *
   * @param  {Object} event     The click event object.
   */
  handleVoteClicked(event) {
    const processFraudStrategy = this.processFraudStrategy;
    // Get Config service.
    const configService = NGXD.getServiceContainer().getService('config');
    // we need to login to FB in the following situations
    if (
      configService.get('strategies.voting') === Enums.STRATEGY_VOTING_POSTWALL ||
      configService.get('voting.requirePerm') === true
    ) {
      fbLogin(
        function (response) {
          if (response.authResponse && response.status === 'connected') {
            // Process Fraud strategy.
            processFraudStrategy();
          }
        },
        { timing: '' }
      );
    } else {
      // Process Fraud strategy.
      processFraudStrategy();
    }

    // Stop event propagation. We pu it AFTER the FB login, because otherwise the FB login popup will be
    // blocked by browser
    event.stopPropagation();
  }

  /**
   * Handler called when a Captcha is required.
   *
   */
  handleShowRecaptchaOverlay() {
    // Update internal state (no need for this to be in Redux)
    this.setState({ showRecaptcha: true });
  }

  /**
   * Handler called when captcha has been completed.
   *
   * @param {object} response The Captcha response object.
   */
  handleCaptchaResponse(response) {
    // Submit vote.
    this.processVotingStrategy(response);

    // Close Recaptcha overlay.
    this.handleCloseCaptchaOverlay(true);
  }

  /**
   * Handler when captcha overlay is closed by pressing outside of the modal, or on the "X" icon
   * We need this method and cannot use the handleCloseCaptchaOverlay, because if the user closes the captcha overlay, the
   * handler in overlay will not pass any parameter into handleCloseCaptchaOverlay, and we will not know whether the
   * captcha had been passed or cancelled.
   */
  handleCancelCaptchaOverlay() {
    this.setState({ showRecaptcha: false });
  }

  /**
   * Handler called when captcha overlay is closed .
   *
   * @param {Boolean} valid Boolean indicating if the Captcha was completed successfully or aborted.
   */
  handleCloseCaptchaOverlay(valid) {
    // Update internal state (no need for this to be in Redux)
    this.setState({ showRecaptcha: false });

    // Dispatch action to update global state.
    this.props.dispatch.hideDynRecaptcha();

    // If user closed Recaptcha overlay without completing it.
    if (!valid) {
      // Common close logic.
      this.handleCloseOverlay();

      // Dispatch action to abort voting.
      this.props.dispatch.abortVote();
    }
  }

  /**
   * Handler called when post-vote message overlay is closed.
   *
   */
  handleClosePostVoteOverlay() {
    // Common close logic.
    this.handleCloseOverlay();

    // Dispatch action.
    this.props.dispatch.hidePostVoteCTA();
  }

  /**
   * Handler called when message overlay is closed.
   *
   */
  handleCloseMessageOverlay() {
    // Common close logic.
    this.handleCloseOverlay();

    // Dispatch action.
    this.props.dispatch.hideMessage();
  }

  /**
   * Handler called when any overlay is closed.
   *
   */
  handleCloseOverlay() {
    // Update internal state (no need for this to be in Redux)
    this.setState({ isVoting: false });
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Action dispatcher proxy methods */

  /**
   * Looks at the fraud strategy value (if any) defined for the campaign, and routes to corresponding logic/actions as appropriate.
   *
   */
  processFraudStrategy() {
    // Update internal state (no need for this to be in Redux)
    this.setState({ isVoting: true });

    // Get Config service.
    const configService = NGXD.getServiceContainer().getService('config');

    // Get the fraud strategy.
    const _fraudStrategy = configService.get('strategies.votingFraud');

    // Act appropriately based on the fraud strategy.
    switch (_fraudStrategy) {
      // Recaptcha and Turnstile
      case Enums.STRATEGY_FRAUD_CAPTCHA:
      case Enums.STRATEGY_FRAUD_TURNSTILE:
        // Display recaptcha overlay.
        this.handleShowRecaptchaOverlay();

        break;

      // Dynamic recaptcha.
      case Enums.STRATEGY_FRAUD_DYNCAPTCHA:
      case Enums.STRATEGY_FRAUD_DYNCAPTCHA_TURNSTILE:
        // Process Voting strategy.
        this.processVotingStrategy();

        break;

      // None.
      case Enums.STRATEGY_FRAUD_NONE:
      default:
        // Process Voting strategy.
        this.processVotingStrategy();

        break;
    }
  }

  /**
   * Looks at the voting strategy value (if any) defined for the campaign, and routes to corresponding logic/actions as appropriate.
   *
   * @param {object} response The Captcha response object.
   */
  processVotingStrategy(captchaResponse) {
    // Get Config service.
    const configService = NGXD.getServiceContainer().getService('config');

    // Get the voting strategy.
    const _votingStrategy = configService.get('strategies.voting');
    const _fraudStrategy = configService.get('strategies.votingFraud');

    // Act appropriately based on the voting strategy (pre vote action).
    switch (_votingStrategy) {
      // Post to wall.
      case Enums.STRATEGY_VOTING_POSTWALL:
        break;

      // Post vote CTA.
      case Enums.STRATEGY_VOTING_CTA:
        break;
    }

    // Finally call submit vote.
    this.submitVote(_votingStrategy, _fraudStrategy, captchaResponse);
  }

  /**
   * Disptaches action to vote.
   *
   * @param  {String} votingStrategy  The voting strategy.
   * @param  {String} fraudStrategy   The fraud strategy.
   * @param  {Object} captchaResponse The Captcha response object.
   */
  submitVote(votingStrategy, fraudStrategy, captchaResponse) {
    // Do not submit vote if already voting!
    if (!this.props.voting || this.props.showDynRecaptcha) {
      const itemOpts = {
        ...this.props.itemOpts,
        shareContext: votingStrategy === Enums.STRATEGY_VOTING_POSTWALL && NGX.App.api.share.campaign.data.context,
      };
      // Submit vote.
      this.props.dispatch.vote(
        this.props.item,
        itemOpts,
        this.props.votes,
        votingStrategy,
        fraudStrategy,
        captchaResponse
      );
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item The DisplayItem being voted on.
 * @property {Object} itemOpts Object defining the various item options.
 * @property {Boolean} showVoteButton Boolean indicating if we should render the vote button.
 * @property {Boolean} showVoteCount Boolean indicating if we should render the vote count.
 * @property {Integer} votes Total number of votes.
 * @property {Boolean} voting Boolean indicating if we are currently performing a voting action.
 * @property {Boolean} showDynRecaptcha Boolean indicating if we need to show a Captcha overlay.
 * @property {String} postVoteCTAContent String containing element markup to render inside an overlay, after a successful vote.
 * @property {String} message String containing a message from the server to  render inside an overlay.
 * @property {Boolean} voted Boolean indicating if we have already voted or not.
 */
Voting.propTypes = {
  item:               PropTypes.instanceOf(DisplayItem),
  itemOpts:           PropTypes.object,
  showVoteButton:     PropTypes.bool,
  showVoteCount:      PropTypes.bool,
  votes:              PropTypes.number,
  voting:             PropTypes.bool,
  showDynRecaptcha:   PropTypes.bool,
  postVoteCTAContent: PropTypes.string,
  message:            PropTypes.string,
  voted:              PropTypes.bool,
};

/**
 * Maps Redux state values to React component props. Called automatically whenever
 * the bound state changes.
 *
 * @private
 *
 * @param  {Object} state The Redux state.
 * @param  {Object} props The current React component props.
 * @return {Object} Updated React component props.
 */
const mapStateToProps = function (state, ownProps) {
  // Get handle on the item.
  const _item = ownProps.item;

  // Look for an updated vote count matching the item id.
  let _votes = Selectors.getVotes(state, _item.id);

  // Has the user already voted on this item id?
  let _voted = false;
  if (_votes) {
    _voted = true;
  }

  // If not found, then fallback to the vote count that the item already has.
  if (!_votes) {
    _votes = ownProps.votes ? ownProps.votes : 0;
  }

  // Is this item currently voting?
  let _isVoting;
  _isVoting = Selectors.getIsVoting(state);

  // Does this item need to show a dynamic Recaptcha overlay?
  let _showDynRecaptcha;
  _showDynRecaptcha = Selectors.getShowDynRecaptcha(state);

  // Do we need to show the post vote CTA?
  let _postVoteCTAContent;
  _postVoteCTAContent = Selectors.getPostVoteCTAContent(state);

  // Get any messsages returned from server.
  let _message;
  _message = Selectors.getMessage(state);

  return {
    votes:              _votes,
    voting:             _isVoting,
    showDynRecaptcha:   _showDynRecaptcha,
    postVoteCTAContent: _postVoteCTAContent,
    message:            _message,
    voted:              _voted,
  };
};

/**
 * Binds Redux action dispatches to React component props.
 *
 * @private
 *
 * @param  {Function} dispatch Dispatch context.
 * @return {Object} The mapped functions.
 */
const mapDispatchToProps = function (dispatch) {
  return {
    dispatch: {
      vote: function (item, itemOpts, votes, votingStrategy, fraudStrategy, captchaResponse) {
        dispatch(Actions.vote(item, itemOpts, votes, votingStrategy, fraudStrategy, captchaResponse));
      },

      abortVote: function () {
        dispatch(Actions.abortVote());
      },

      posttowall: function (item, itemOpts) {
        dispatch(Actions.posttowall(item, itemOpts));
      },

      hideDynRecaptcha: function () {
        dispatch(Actions.hideDynRecaptcha());
      },

      hidePostVoteCTA: function () {
        dispatch(Actions.hidePostVoteCTA());
      },

      hideMessage: function () {
        dispatch(Actions.hideMessage());
      },
    },
  };
};

// Generate connected/smart component.
const VotingConnected = connect(mapStateToProps, mapDispatchToProps)(Voting);

// Export the connected/smart component as default.
export default VotingConnected;

// Also export the dumb component (for unit testing, doc preview etc).
export { Voting as docPreviewExport };
